'use client';

import { ReactNode } from 'react';
import { clsx } from 'clsx';
import { HighlightText } from './search-highlight';
import {
  ArticleTitleButton,
  ARTICLE_TITLE_MENU_CLASS,
} from './article-title-button';

export interface ArticleNameBlockProps {
  articleNumber: string;
  description: string | null | undefined;
  description2?: string | null;
  variant?: 'compact' | 'default' | 'large';
  highlight?: boolean;
  linkArticle?: boolean;
  /**
   * When set, the title renders as an inline button firing this handler
   * (e.g. opening an action menu) instead of the shop link. Takes precedence
   * over `linkArticle`, so callers may pass `linkArticle` as a fallback.
   */
  onTitleClick?: () => void;
  className?: string;
  /** Replaces the variant's title classes when set; otherwise appended. Use `descriptionClassName` for full override. */
  titleClassName?: string;
  /** Full override of variant's title text classes (font, color, size). */
  descriptionClassName?: string;
  /** Full override of variant's description2 text classes. */
  description2ClassName?: string;
  /** Full override of variant's articleNumber text classes. */
  articleNumberClassName?: string;
  /** Optional content rendered before the title text (e.g. a status icon). */
  titlePrefix?: ReactNode;
  /** Optional content appended next to the title (e.g. an icon). */
  titleSlot?: ReactNode;
  /** Truncate description to a single line instead of allowing 2 lines. */
  singleLineTitle?: boolean;
  /** Hide the article number row (use when it's rendered elsewhere). */
  hideArticleNumber?: boolean;
  /**
   * Hersteller, angezeigt hinter der Artikelnummer ("N01V106 | Rheinzink").
   * Grund: Die Suche findet Artikel über den Herstellernamen, der Name stand
   * aber in keiner Trefferzeile — der Treffer war dadurch nicht erkennbar.
   * Wird wie die anderen Texte hervorgehoben, wenn `highlight` gesetzt ist.
   * Ohne Artikelnummer-Zeile (`hideArticleNumber`) entfällt er mit.
   */
  manufacturer?: string | null;
}

const VARIANT_CLASSES = {
  compact: {
    title: 'text-xs font-medium text-gray-900',
    sub: 'text-[11px] text-gray-600',
    number: 'text-[10px] text-gray-500 font-mono',
  },
  default: {
    title: 'text-sm font-medium text-gray-900',
    sub: 'text-xs text-gray-600',
    number: 'text-xs text-gray-500 font-mono',
  },
  large: {
    title: 'text-sm font-semibold text-gray-900',
    sub: 'text-xs text-gray-600',
    number: 'text-xs text-gray-500 font-mono',
  },
} as const;

export function ArticleNameBlock({
  articleNumber,
  description,
  description2,
  variant = 'default',
  highlight = false,
  linkArticle = false,
  onTitleClick,
  className,
  titleClassName,
  descriptionClassName,
  description2ClassName,
  articleNumberClassName,
  titlePrefix,
  titleSlot,
  singleLineTitle = false,
  hideArticleNumber = false,
  manufacturer,
}: ArticleNameBlockProps) {
  const cls = VARIANT_CLASSES[variant];
  const renderText = (text: string) =>
    highlight ? <HighlightText text={text} /> : text;

  const titleText = description || '-';
  const titleNode = renderText(titleText);
  const titleClamp = singleLineTitle ? 'truncate' : 'line-clamp-2';
  const titleStyle = descriptionClassName ?? cls.title;
  const subStyle = description2ClassName ?? cls.sub;
  const numberStyle = articleNumberClassName ?? cls.number;

  const titleBody = onTitleClick ? (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        e.preventDefault();
        onTitleClick();
      }}
      aria-haspopup="dialog"
      aria-label={`Aktionen für ${titleText} anzeigen`}
      title="Aktionen anzeigen"
      className={ARTICLE_TITLE_MENU_CLASS}
    >
      {titleNode}
    </button>
  ) : linkArticle ? (
    <ArticleTitleButton articleNumber={articleNumber}>
      {titleNode}
    </ArticleTitleButton>
  ) : (
    titleNode
  );

  return (
    <div className={clsx('min-w-0', className)}>
      {titlePrefix ? (
        <div className={clsx('flex items-center gap-1.5', titleStyle, titleClassName)}>
          {titlePrefix}
          <span className={clsx('min-w-0', titleClamp)}>{titleBody}</span>
          {titleSlot}
        </div>
      ) : (
        <div className={clsx(titleStyle, titleClamp, titleClassName)}>
          {titleBody}
          {titleSlot}
        </div>
      )}
      {description2 && (
        <div className={clsx(subStyle, 'line-clamp-2 mt-0.5')}>
          {renderText(description2)}
        </div>
      )}
      {!hideArticleNumber && (
        <div className={clsx(numberStyle, 'mt-0.5')}>
          {renderText(articleNumber)}
          {manufacturer && (
            <>
              {/* Trenner nur optisch — Screenreader lesen "N01V106 Rheinzink". */}
              <span className="mx-1 text-gray-300" aria-hidden="true">
                |
              </span>
              {/* font-sans hebt die Monospace der Artikelnummer auf: ein
                  Markenname ist kein Code. */}
              <span className="font-sans">{renderText(manufacturer)}</span>
            </>
          )}
        </div>
      )}
    </div>
  );
}
